'use client'

import { useEffect, useMemo, useState } from 'react'

import Icon, { IconName } from '@/components/atomic/icon'
import { BodyXS } from '@/components/atomic/text'

import { CountdownTimerContainer } from './styles'
import { calculateTimeLeft, formatRemainingTime } from './utils'

export enum CountdownVariant {
  Default = 'default',
  Warning = 'warning',
  Danger = 'danger',
}

export enum RfqTimeType {
  DueTime = 'due_time',
  FirmUntilTime = 'firm_until_time',
  ExpireTime = 'expire_time',
}

interface CountdownTimerProps {
  timestamp: string
  showIcon?: boolean
  showLabel?: boolean
  rfqTimeType: RfqTimeType
}

export const CountdownTimer = ({
  timestamp,
  showIcon = false,
  showLabel = false,
  rfqTimeType,
}: CountdownTimerProps) => {
  // Initialize with pre-calculated seconds remaining until target timestamp
  const [secondsLeft, setSecondsLeft] = useState(calculateTimeLeft(timestamp))

  // Set up timer that updates remaining seconds every second
  useEffect(() => {
    // Need this first call to ensure the timer doesn't show 0:00 before showing the actual time
    setSecondsLeft(calculateTimeLeft(timestamp))
    const timer = setInterval(() => {
      setSecondsLeft(calculateTimeLeft(timestamp))
    }, 1000)

    // Clean up interval on component unmount
    return () => clearInterval(timer)
  }, [timestamp])

  // Determine visual style based on time type (due, firm until, or expiry)
  const variant = useMemo(() => {
    switch (true) {
      case rfqTimeType === RfqTimeType.ExpireTime:
        return CountdownVariant.Danger
      case rfqTimeType === RfqTimeType.FirmUntilTime:
        return CountdownVariant.Warning
      case rfqTimeType === RfqTimeType.DueTime:
        return CountdownVariant.Default
      default:
        return CountdownVariant.Default
    }
  }, [rfqTimeType])

  // Set appropriate label text based on time type
  const label = useMemo(() => {
    switch (true) {
      case rfqTimeType === RfqTimeType.ExpireTime:
        return 'Expires in'
      case rfqTimeType === RfqTimeType.FirmUntilTime:
        return 'Firm until'
      case rfqTimeType === RfqTimeType.DueTime:
        return 'Due in'
      default:
        return ''
    }
  }, [rfqTimeType])

  return (
    <CountdownTimerContainer variant={variant}>
      {showLabel && <BodyXS>{label}</BodyXS>}
      {showIcon && <Icon iconName={IconName.ClockIcon} size="small" outline />}
      <BodyXS>{formatRemainingTime(secondsLeft)}</BodyXS>
    </CountdownTimerContainer>
  )
}
